import { useMachine } from "@xstate/react";
import { Ellipsis, Search, Settings, SquarePen, X } from "lucide-react";
import React, { type FormEvent } from "react";
import "./shell.css";

import type { T_ChatV8ThreadListItem } from "@lattice/core-lib/types/chat-v8";
import {
  ShellHistoryPanel,
  type T_ShellHistoryPanelModel,
} from "@lattice/shared-frontend/components";
import {
  threadActionMachine,
  type T_ThreadActionTarget,
} from "@lattice/shared-frontend/shell";

import { Button } from "@lattice/frontend/components/kit/Button";
import { DropdownMenu } from "@lattice/frontend/components/kit/DropdownMenu";
import { IconButton } from "@lattice/frontend/components/kit/IconButton";
import { Sheet } from "@lattice/frontend/components/kit/Sheet";
import { TextField } from "@lattice/frontend/components/kit/TextField";

export const SHELL_HISTORY_RAIL_ID = "shell-conversation-history";

const THREAD_ACTION_ITEMS = [
  { id: "rename", label: "Rename" },
  { id: "delete", label: "Delete" },
] as const;

const EMPTY_STATE_COPY = {
  empty: "No conversations yet.",
  loading: "Loading chats…",
  "no-matches": "No conversations match your search.",
} as const;

export type T_ThreadHistoryRailProps = {
  activeThreadCuid: string | null;
  /** True only on the new-chat route; drives the New chat button's fill. */
  newChatActive?: boolean;
  onDeleteThread: (threadCuid: string) => Promise<void>;
  onNewChat: () => void;
  onOpenSettings: () => void;
  onRenameThread: (threadCuid: string, name: string) => Promise<void>;
  onSelectThread: (threadCuid: string) => void;
  open: boolean;
  threads: T_ChatV8ThreadListItem[];
};

function getThreadActionErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return "Something went wrong. Please try again.";
}

/**
 * Web pieces for the shared ShellHistoryPanel composer (also used by the
 * mobile ChatHistoryDrawer). The composer owns section order, search state,
 * filtering, and row preparation; this file owns the rail chrome, kit
 * controls, and the rename/delete sheet. Renders as a collapsible left rail
 * at desktop widths and an over-canvas drawer on narrow web viewports.
 */
export function ThreadHistoryRail({
  activeThreadCuid,
  newChatActive = false,
  onDeleteThread,
  onNewChat,
  onOpenSettings,
  onRenameThread,
  onSelectThread,
  open,
  threads,
}: T_ThreadHistoryRailProps) {
  const [actionSnapshot, sendAction] = useMachine(threadActionMachine);
  const actionTarget = actionSnapshot.context.thread;
  const renaming =
    actionSnapshot.matches("renaming") ||
    actionSnapshot.matches("savingRename");
  const actionPending =
    actionSnapshot.matches("savingRename") ||
    actionSnapshot.matches("deleting");

  const handleRename = (event?: FormEvent<HTMLFormElement>) => {
    event?.preventDefault();
    if (!actionSnapshot.matches("renaming") || actionTarget === null) return;
    const name = actionSnapshot.context.newName.trim();
    if (name.length === 0) return;
    const threadCuid = actionTarget.cuid;
    sendAction({ type: "CONFIRM_RENAME" });
    void onRenameThread(threadCuid, name)
      .then(() => sendAction({ type: "RENAME_OK" }))
      .catch((error: unknown) =>
        sendAction({
          type: "RENAME_ERR",
          error: getThreadActionErrorMessage(error),
        }),
      );
  };

  const handleDelete = () => {
    if (!actionSnapshot.matches("confirmingDelete") || actionTarget === null) {
      return;
    }
    const threadCuid = actionTarget.cuid;
    sendAction({ type: "CONFIRM_DELETE" });
    void onDeleteThread(threadCuid)
      .then(() => sendAction({ type: "DELETE_OK" }))
      .catch((error: unknown) =>
        sendAction({
          type: "DELETE_ERR",
          error: getThreadActionErrorMessage(error),
        }),
      );
  };

  const renderThreadList = ({ emptyState, rows }: T_ShellHistoryPanelModel) => (
    <>
      <p className="shell-rail__header">Conversations</p>
      <div className="shell-rail__list">
        {rows.length === 0 ? (
          <p className="shell-rail__empty">{EMPTY_STATE_COPY[emptyState]}</p>
        ) : (
          rows.map((row) => {
            const target: T_ThreadActionTarget = {
              cuid: row.threadCuid,
              name: row.displayName,
            };
            return (
              <div
                className={[
                  "shell-rail__thread-row",
                  row.isActive ? "shell-rail__thread--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
                key={row.threadCuid}
              >
                <button
                  aria-current={row.isActive ? "page" : undefined}
                  className="shell-rail__thread"
                  onClick={() => onSelectThread(row.threadCuid)}
                  tabIndex={open ? 0 : -1}
                  type="button"
                >
                  <span className="shell-rail__thread-label">
                    {row.displayName}
                  </span>
                </button>
                <div className="shell-rail__thread-actions">
                  <DropdownMenu
                    align="right"
                    items={THREAD_ACTION_ITEMS}
                    menuAccessibilityLabel={`Actions for ${row.displayName}`}
                    menuWidth={152}
                    onSelect={(item) => {
                      sendAction({
                        type:
                          item.id === "rename" ? "OPEN_RENAME" : "OPEN_DELETE",
                        thread: target,
                      });
                    }}
                    renderTrigger={() => (
                      <Ellipsis aria-hidden size={18} strokeWidth={2} />
                    )}
                    triggerAccessibilityLabel={`Actions for ${row.displayName}`}
                  />
                </div>
              </div>
            );
          })
        )}
      </div>
    </>
  );

  return (
    <>
      <ShellHistoryPanel
        activeThreadCuid={activeThreadCuid}
        newChatActive={newChatActive}
        slots={{
          renderContainer: (sections) => (
            <nav
              aria-hidden={!open}
              aria-label="Conversation history"
              className={["shell-rail", open ? "" : "shell-rail--closed"]
                .filter(Boolean)
                .join(" ")}
              id={SHELL_HISTORY_RAIL_ID}
            >
              {sections}
            </nav>
          ),
          renderFooter: () => (
            <footer className="shell-rail__footer">
              <Button
                disabled={!open}
                label="Settings"
                leading={<Settings aria-hidden size={16} />}
                onClick={onOpenSettings}
                size="compact"
                variant="quiet"
              />
            </footer>
          ),
          renderHeader: () => (
            <div aria-label="Lattice" className="shell-rail__brand">
              Lattice
            </div>
          ),
          renderNewChat: ({ newChatActive: active }) => (
            <div className="shell-rail__new-chat">
              <Button
                disabled={!open}
                label="New chat"
                leading={<SquarePen aria-hidden size={17} />}
                onClick={onNewChat}
                size="compact"
                variant={active ? "secondary" : "quiet"}
              />
            </div>
          ),
          renderSearch: ({ onSearchQueryChange, searchQuery }) => (
            <div className="shell-rail__search">
              <TextField
                ariaLabel="Search for chats"
                disabled={!open}
                embedded
                leading={<Search aria-hidden size={18} />}
                onChange={(event) => onSearchQueryChange(event.target.value)}
                placeholder="Search for chats"
                trailing={
                  searchQuery.length > 0 ? (
                    <IconButton
                      ariaLabel="Clear chat search"
                      disabled={!open}
                      icon={<X aria-hidden size={16} />}
                      onClick={() => onSearchQueryChange("")}
                      size="compact"
                      variant="quiet"
                    />
                  ) : undefined
                }
                type="search"
                value={searchQuery}
              />
            </div>
          ),
          renderThreadList,
        }}
        threads={threads}
      />
      <Sheet
        closeOnBackdropPress={!actionPending}
        onClose={() => {
          if (!actionPending) sendAction({ type: "CLOSE" });
        }}
        title={renaming ? "Rename chat" : "Delete chat"}
        visible={!actionSnapshot.matches("idle")}
      >
        {renaming ? (
          <form className="shell-thread-action" onSubmit={handleRename}>
            <div className="shell-thread-action__copy">
              <h2>Rename chat</h2>
              <p>Choose a name that will be easy to find later.</p>
            </div>
            <TextField
              ariaLabel="Chat name"
              autoFocus
              disabled={actionPending}
              error={actionSnapshot.context.errorMessage ?? undefined}
              onChange={(event) =>
                sendAction({
                  type: "RENAME_INPUT",
                  newName: event.target.value.slice(0, 80),
                })
              }
              value={actionSnapshot.context.newName}
            />
            <div className="shell-thread-action__buttons">
              <Button
                disabled={actionPending}
                label="Cancel"
                onClick={() => sendAction({ type: "CLOSE" })}
                variant="secondary"
              />
              <Button
                disabled={actionSnapshot.context.newName.trim().length === 0}
                label="Save"
                loading={actionSnapshot.matches("savingRename")}
                onClick={() => handleRename()}
              />
            </div>
          </form>
        ) : (
          <div className="shell-thread-action">
            <div className="shell-thread-action__copy">
              <h2>Delete chat?</h2>
              <p>
                “{actionTarget?.name ?? "This chat"}” will be permanently
                deleted. This cannot be undone.
              </p>
            </div>
            {actionSnapshot.context.errorMessage ? (
              <p className="shell-thread-action__error" role="alert">
                {actionSnapshot.context.errorMessage}
              </p>
            ) : null}
            <div className="shell-thread-action__buttons">
              <Button
                disabled={actionPending}
                label="Cancel"
                onClick={() => sendAction({ type: "CLOSE" })}
                variant="secondary"
              />
              <Button
                label="Delete"
                loading={actionSnapshot.matches("deleting")}
                onClick={handleDelete}
                variant="danger"
              />
            </div>
          </div>
        )}
      </Sheet>
    </>
  );
}
